'use client'

import { forwardRef, useState, useEffect, useRef, ReactNode } from 'react'
import { Eye, EyeOff, AlertCircle, CheckCircle2, Info, Search, X } from 'lucide-react'
import { safeAccess } from '../utils/security'

/**
 * BIRHAUS Input Component
 * 
 * Enhanced input implementing all 10 BIRHAUS principles:
 * - Progressive validation with Spanish-first error messages
 * - Cognitive load reduction through clear visual feedback
 * - Auto-save functionality with status indicators
 * - Accessibility compliance (WCAG AA+)
 * - Spanish-first labeling and error handling
 * - Performance optimized with debounced validation
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface BirhausInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix' | 'autoSave'> {
  // BIRHAUS Principle #7: Bilingual by design
  label?: string
  labelEs?: string
  labelEn?: string
  
  // BIRHAUS Principle #4: Progressive disclosure
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // BIRHAUS Principle #2: Honest data
  error?: string
  errorEs?: string
  errorEn?: string
  
  // Spanish-first error patterns: "Qué pasó + cómo resolver"
  solution?: string
  solutionEs?: string
  solutionEn?: string
  
  // BIRHAUS Principle #8: Performance is UX
  autoSave?: boolean
  autoSaveDelay?: number
  onAutoSave?: (value: string) => Promise<void>
  
  // Progressive validation
  validateOnChange?: boolean
  validateOnBlur?: boolean
  validationRules?: ValidationRule[]
  
  // BIRHAUS Principle #1: Form serves flow
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'filled' | 'minimal'
  
  // Enhanced functionality
  prefix?: ReactNode
  suffix?: ReactNode
  showPasswordToggle?: boolean
  showClearButton?: boolean
  
  // BIRHAUS Principle #6: Accessibility = dignity
  helpText?: string
  helpTextEs?: string
  helpTextEn?: string
  
  // Status indicators
  loading?: boolean
  success?: boolean
  
  // Cognitive load tracking
  fieldPriority?: 'required' | 'optional' | 'progressive'
  cognitiveWeight?: number
  
  // v2.0 Compliance and Localization Props
  compliance?: string
  locale?: string
  auditTrail?: boolean
  currency?: boolean
  currencySymbol?: string
  thousandsSeparator?: string
  decimalSeparator?: string
  maxDecimals?: number
  validateOn?: 'blur' | 'change' | 'submit'
  customValidation?: (value: string) => string | null
}

// Import ValidationRule from central types to resolve conflicts
import type { ValidationRule } from '@birhaus/types/validation'
export type { ValidationRule }

export const BirhausInput = forwardRef<HTMLInputElement, BirhausInputProps>(({
  // Labels (Spanish-first)
  label,
  labelEs,
  labelEn,
  
  // Descriptions
  description,
  descriptionEs,
  descriptionEn,
  
  // Error handling
  error,
  errorEs,
  errorEn,
  solution,
  solutionEs,
  solutionEn,
  
  // Auto-save
  autoSave = false,
  autoSaveDelay = 1000,
  onAutoSave,
  
  // Validation
  validateOnChange = true,
  validateOnBlur = true,
  validationRules = [],
  
  // Appearance
  size = 'md',
  variant = 'default',
  
  // Enhanced props
  prefix,
  suffix,
  showPasswordToggle = false,
  showClearButton = false,
  
  // Help text
  helpText,
  helpTextEs,
  helpTextEn,
  
  // Status
  loading = false,
  success = false,
  
  // Cognitive load
  fieldPriority = 'optional',
  cognitiveWeight = 1,
  
  // v2.0 Compliance and Localization Props
  compliance,
  locale = 'es-PY',
  auditTrail = false,
  currency = false,
  currencySymbol = 'Gs.',
  thousandsSeparator = '.',
  decimalSeparator = ',',
  maxDecimals = 2,
  validateOn = 'blur',
  customValidation,
  
  // Standard props
  className,
  type = 'text',
  value,
  defaultValue,
  onChange,
  onBlur,
  onFocus,
  disabled,
  required,
  ...props
}, ref) => {
  
  // BIRHAUS: State management
  const [currentValue, setCurrentValue] = useState(value || defaultValue || '')
  const [showPassword, setShowPassword] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [validationError, setValidationError] = useState<string>('')
  const [validationSolution, setValidationSolution] = useState<string>('')
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout>()
  const inputRef = useRef<HTMLInputElement>(null)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displayLabel = resolveText(labelEs, labelEn, label)
  const displayDescription = resolveText(descriptionEs, descriptionEn, description)
  const displayError = resolveText(errorEs, errorEn, error) || validationError
  const displaySolution = resolveText(solutionEs, solutionEn, solution) || validationSolution
  const displayHelpText = resolveText(helpTextEs, helpTextEn, helpText)
  
  // BIRHAUS: Progressive validation
  const validateValue = (val: string) => {
    if (!validationRules.length) return
    
    for (const rule of validationRules) {
      if (!rule.test(val)) {
        setValidationError(rule.errorEs || rule.errorEn || '')
        setValidationSolution(rule.solutionEs || rule.solutionEn || '')
        return
      }
    }
    
    setValidationError('')
    setValidationSolution('')
  }
  
  // BIRHAUS: Auto-save functionality
  const triggerAutoSave = (val: string) => {
    if (!autoSave || !onAutoSave) return
    
    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current)
    }
    
    // Set new timeout
    autoSaveTimeoutRef.current = setTimeout(async () => {
      setAutoSaveStatus('saving')
      try {
        await onAutoSave(val)
        setAutoSaveStatus('saved')
        setTimeout(() => setAutoSaveStatus('idle'), 2000)
      } catch (error) {
        setAutoSaveStatus('error')
        setTimeout(() => setAutoSaveStatus('idle'), 3000)
      }
    }, autoSaveDelay)
  }
  
  // Handle value change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    setCurrentValue(newValue)
    
    // Progressive validation
    if (validateOnChange) {
      validateValue(newValue)
    }
    
    // Auto-save
    if (autoSave) {
      triggerAutoSave(newValue)
    }
    
    onChange?.(event)
  }
  
  // Handle blur
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    
    if (validateOnBlur) {
      validateValue(currentValue as string)
    }
    
    onBlur?.(event)
  }
  
  // Handle focus
  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    onFocus?.(event)
  }
  
  // Clear input
  const handleClear = () => {
    setCurrentValue('')
    setValidationError('')
    setValidationSolution('')
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current)
      }
    }
  }, [])
  
  // BIRHAUS Principle #9: Consistency via tokens
  const sizeClasses = {
    sm: 'h-8 px-3 text-sm',
    md: 'h-10 px-4 text-sm',
    lg: 'h-12 px-4 text-base'
  }
  
  const variantClasses = {
    default: 'border border-gray-300 bg-white',
    filled: 'border-0 bg-gray-100',
    minimal: 'border-0 border-b border-gray-300 bg-transparent rounded-none'
  }
  
  // BIRHAUS: Error state styling
  const hasError = !!(displayError || error)
  const hasSuccess = success && !hasError
  
  const stateClasses = hasError 
    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
    : hasSuccess
    ? 'border-green-500 focus:border-green-500 focus:ring-green-500'
    : 'focus:border-blue-500 focus:ring-blue-500'
  
  // Priority visual indicators
  const priorityClasses = {
    required: 'ring-2 ring-red-100',
    optional: '',
    progressive: 'ring-1 ring-blue-100'
  }
  
  const baseInputClasses = cn(
    'w-full rounded-lg transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-1',
    'disabled:cursor-not-allowed disabled:opacity-50',
    'placeholder:text-gray-400',
    sizeClasses[size],
    variantClasses[variant],
    stateClasses,
    priorityClasses[fieldPriority],
    className
  )
  
  return (
    <div className="space-y-2">
      {/* Label and description */}
      {displayLabel && (
        <div className="flex items-center justify-between">
          <label 
            htmlFor={props.id}
            className={cn(
              'block text-sm font-medium text-gray-700',
              required && "after:content-['*'] after:ml-1 after:text-red-500"
            )}
          >
            {displayLabel}
            {fieldPriority === 'required' && (
              <span className="ml-1 text-xs text-red-600">(requerido)</span>
            )}
            {fieldPriority === 'optional' && (
              <span className="ml-1 text-xs text-gray-500">(opcional)</span>
            )}
          </label>
          
          {/* Auto-save status */}
          {autoSave && (
            <div className="flex items-center gap-1 text-xs">
              {autoSaveStatus === 'saving' && (
                <span className="text-blue-600">Guardando...</span>
              )}
              {autoSaveStatus === 'saved' && (
                <span className="text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Guardado
                </span>
              )}
              {autoSaveStatus === 'error' && (
                <span className="text-red-600">Error al guardar</span>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Description */}
      {displayDescription && (
        <p className="text-sm text-gray-600">{displayDescription}</p>
      )}
      
      {/* Input container */}
      <div className="relative">
        {/* Prefix */}
        {prefix && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            {prefix}
          </div>
        )}
        
        {/* Input */}
        <input
          ref={ref || inputRef}
          type={showPasswordToggle ? (showPassword ? 'text' : 'password') : type}
          value={value || currentValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          disabled={disabled || loading}
          required={required}
          className={cn(
            baseInputClasses,
            prefix ? 'pl-10' : '',
            (suffix || showPasswordToggle || showClearButton) ? 'pr-10' : ''
          )}
          {...props}
        />
        
        {/* Loading indicator */}
        {loading && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
          </div>
        )}
        
        {/* Success indicator */}
        {hasSuccess && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </div>
        )}
        
        {/* Error indicator */}
        {hasError && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <AlertCircle className="h-4 w-4 text-red-500" />
          </div>
        )}
        
        {/* Password toggle */}
        {showPasswordToggle && type === 'password' && !loading && !hasError && !hasSuccess && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
        
        {/* Clear button */}
        {showClearButton && currentValue && !loading && !hasError && !showPasswordToggle && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            aria-label="Limpiar campo"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        
        {/* Suffix */}
        {suffix && !showPasswordToggle && !showClearButton && !loading && !hasError && !hasSuccess && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
            {suffix}
          </div>
        )}
      </div>
      
      {/* Error message with solution */}
      {displayError && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-sm">
            <p className="text-red-800 font-medium">Qué pasó: {displayError}</p>
            {displaySolution && (
              <p className="text-red-700 mt-1">Cómo resolver: {displaySolution}</p>
            )}
          </div>
        </div>
      )}
      
      {/* Help text */}
      {displayHelpText && !displayError && (
        <div className="flex items-start gap-2 text-sm text-gray-600">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <p>{displayHelpText}</p>
        </div>
      )}
    </div>
  )
})

BirhausInput.displayName = 'BirhausInput'

// BIRHAUS: Common validation rules in Spanish
export const birhausValidationRules = {
  required: (fieldName: string): ValidationRule => ({
    test: (value) => value.trim().length > 0,
    errorEs: `${fieldName} es requerido`,
    errorEn: `${fieldName} is required`,
    solutionEs: `Ingrese un valor para ${fieldName.toLowerCase()}`,
    solutionEn: `Enter a value for ${fieldName.toLowerCase()}`
  }),
  
  email: (): ValidationRule => ({
    test: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    errorEs: 'El formato del email no es válido',
    errorEn: 'Email format is not valid',
    solutionEs: 'Use el formato: usuario@ejemplo.com',
    solutionEn: 'Use the format: user@example.com'
  }),
  
  minLength: (min: number): ValidationRule => ({
    test: (value) => value.length >= min,
    errorEs: `Debe tener al menos ${min} caracteres`,
    errorEn: `Must be at least ${min} characters`,
    solutionEs: `Agregue al menos ${min} caracteres`,
    solutionEn: `Add at least ${min} characters`
  }),
  
  paraguayPhone: (): ValidationRule => ({
    test: (value) => /^(\+595|0)(9[0-9]{8}|[2-9][1-8][0-9]{5,6})$/.test(value.replace(/\s/g, '')),
    errorEs: 'El número de teléfono no es válido para Paraguay',
    errorEn: 'Phone number is not valid for Paraguay',
    solutionEs: 'Use el formato: 0981123456 o +595981123456',
    solutionEn: 'Use the format: 0981123456 or +595981123456'
  }),
  
  ruc: (): ValidationRule => ({
    test: (value) => /^[0-9]{1,8}-[0-9]$/.test(value),
    errorEs: 'El formato de RUC no es válido',
    errorEn: 'RUC format is not valid',
    solutionEs: 'Use el formato: 12345678-9',
    solutionEn: 'Use the format: 12345678-9'
  })
}

// Components and utilities are exported via individual export declarations above